import {Component, computed, DestroyRef, effect, ElementRef, inject, input, OnInit, output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TaAppStore} from '../../../../state/clcta-store';
import {CreateTaStep, CreateTaStepType} from '../../../../util/app.types';
import {CreateTaDocumentationComponent} from '../create-ta-documentation/create-ta-documentation.component';
import {CreateTaItineraryComponent} from '../create-ta-itinerary/create-ta-itinerary.component';
import {CreateTaTravellersComponent} from '../create-ta-travellers/create-ta-travellers.component';
import {CreateTaDetailsComponent} from '../create-ta-details/create-ta-details.component';
import {ActivatedRoute, Router, UrlSegment} from '@angular/router';
import {TaSubmissionDatasource} from '../../../../datasources/ta-submission.datasource';
import {combineLatest} from 'rxjs';
import {LoadingService} from '../../../../services/loading.service';

@Component({
  selector: 'create-ta-step-container',
  imports: [
    CreateTaDocumentationComponent,
    CreateTaItineraryComponent,
    CreateTaTravellersComponent,
    CreateTaDetailsComponent,
  ],
  templateUrl: './create-ta-step-container.component.html',
  styleUrl: './create-ta-step-container.component.scss'
})
export class CreateTaStepContainerComponent implements OnInit {
  currentStep = input<CreateTaStep>();
  isReadOnly = input<boolean>(false);
  // Itinerary step only; input/output (not model) so the wizard's long-lived container holds no selection.
  travellerId = input<string>('');
  travellerIdChange = output<string>();
  taLoaded = computed<boolean>(() => !!this.store.taSubmission())
  private destroyRef = inject(DestroyRef);

  constructor(
    private store: TaAppStore,
    private taSubmissionDatasource: TaSubmissionDatasource,
    private route: ActivatedRoute,
    private loadingService: LoadingService) {
    effect(() => {
      // Locations load on their own request, so the overlay has to wait on both flags - otherwise
      // the itinerary step paints before its location list arrives.
      let isLoading = this.store.isLoadingData() || this.store.locationsLoading();
      if (isLoading) {
        this.loadingService.showLoading({message: 'Loading data...'});
      } else {
        this.loadingService.hideLoading();
    }
    });
  }

  ngOnInit() {
    combineLatest([
      this.route.url,
      this.route.queryParams
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([segments, params]) => {
      const pathParts = segments.map((segment: UrlSegment) => segment.path);
      let isTa = (pathParts[0] === 'ta')
      if (isTa) {
        let isReviewing = pathParts[pathParts.length - 1] === 'review';
        if (isReviewing) {
          this.store.updateCurrentCreateTaStep(CreateTaStepType.REVIEW);
        }
        const submissionId: string = pathParts.length > 1 ? pathParts[1] : ''
        if (submissionId) {
          this.taSubmissionDatasource.nullLoadServerTaSubmission(submissionId);
        }
      }
      let step = this.parseCreateTaStepType(params['step']);
      if (step) {
        this.store.updateCurrentCreateTaStep(step);
      }
    });
  }

  parseCreateTaStepType(type: string): CreateTaStepType | undefined {
    if (Object.values(CreateTaStepType).includes(type as CreateTaStepType)) {
      return type as CreateTaStepType;
    }
    return undefined;
  }

  protected readonly CreateTaStepType = CreateTaStepType;
}
